import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, message, Input, Button, Tag, Popconfirm, Pagination, Spin, Checkbox, Space, Modal, Form, Select, notification, Image as AntImage } from 'antd';
import { SearchOutlined, DeleteOutlined, EyeOutlined, SelectOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons';
import './GalleryPage.css';
import api, { getCurrentUser, changePassword, getBaiduTaskStatus, batchUpdateTags, updateImageMeta, getAllTags } from '../services/api';
import { ImageSearchResult } from '../services/api';

const { Search } = Input;

const GalleryPage: React.FC = () => {
  const [images, setImages] = useState<ImageSearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedImages, setSelectedImages] = useState<number[]>([]);
  const [selectMode, setSelectMode] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingImage, setEditingImage] = useState<any>(null);
  const [form] = Form.useForm();
  const [resyncing, setResyncing] = useState(false);
  const [pageSize, setPageSize] = useState(5);
  const [currentUser, setCurrentUser] = useState<{ username: string; is_admin?: boolean } | null>(null);
  const [pwdModalVisible, setPwdModalVisible] = useState(false);
  const [pwdForm] = Form.useForm();
  const [notifApi, notifCtx] = notification.useNotification();
  const SHOW_BAIDU_PROGRESS = false;
  const pollTimerRef = useRef<number | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewItem, setPreviewItem] = useState<any>(null);
  // 标签筛选
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  // 批量编辑
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkUpdateDesc, setBulkUpdateDesc] = useState(false);
  const [bulkDesc, setBulkDesc] = useState('');
  const [bulkUpdateTags, setBulkUpdateTags] = useState(false);
  const [bulkTags, setBulkTags] = useState('');
  const [bulkTagOperation, setBulkTagOperation] = useState<'replace' | 'add' | 'remove'>('replace');

  // 请求取消与防抖
  const abortRef = useRef<AbortController | null>(null);
  const searchTimerRef = useRef<number | null>(null);

  // 加载图片数据（支持取消）
  const loadImages = async (page = 1, search = '') => {
    setLoading(true);
    try {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const params: any = { page, page_size: pageSize, search, all: true };
      if (selectedTags.length > 0) {
        params.tags = JSON.stringify(selectedTags);
      }
      const { data } = await api.get('/images/', {
        params,
        signal: controller.signal as any,
      });

      const normalized = (data.images || []).map((img: any) => ({
        ...img,
        file_path: `/uploads/${img.filename}`,
        thumb_url: img.thumb_url || `/api/images/${img.id}/thumbnail?size=240&q=70`
      }));

      setImages(normalized);
      setTotal(data.total || 0);
    } catch (error) {
      if ((error as any)?.name !== 'CanceledError') {
        message.error('网络错误，请重试');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 拉取当前用户
    (async () => {
      try {
        const me = await getCurrentUser();
        setCurrentUser(me);
      } catch {}
    })();
  }, []);

  // 拉取全部标签
  useEffect(() => {
    (async () => {
      try {
        const res: any = await getAllTags();
        const list: string[] = Array.isArray(res) ? res : (Array.isArray(res?.tags) ? res.tags : []);
        setAvailableTags(list.filter((t) => typeof t === 'string' && t.trim()).map(t => t.trim()));
      } catch {}
    })();
  }, []);

  // 轻量轮询：每2秒查询当前页图片的百度同步状态（若Redis记录存在）
  useEffect(() => {
    const poll = async () => {
      try {
        const ids = images.map((img) => (img as any).id).filter(Boolean);
        if (ids.length === 0) return;
        const res = await getBaiduTaskStatus(ids);
        const statuses = res?.statuses || {} as Record<number, any>;
        if (Object.keys(statuses).length === 0) return;
        setImages((prev) => prev.map((img: any) => {
          const st = statuses[img.id];
          if (!st) return img;
          if (st.status === 'done' && st.cont_sign) {
            return { ...img, baidu_synced: true, baidu_sign: st.cont_sign };
          }
          if (st.status === 'error') {
            return { ...img, baidu_synced: false };
          }
          return img;
        }));
      } catch {}
    };
    // 启动与清理
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollTimerRef.current = window.setInterval(poll, 2000);
    return () => {
      if (pollTimerRef.current) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [images.map(i => (i as any).id).join(','), currentPage, pageSize]);

  // 删除图片
  const deleteImage = async (imageId: number) => {
    try {
      const response = await api.delete(`/images/${imageId}`);
      if (response.status >= 200 && response.status < 300) {
        message.success('已提交删除（百度后台同步）');
        if (SHOW_BAIDU_PROGRESS) {
          const key = `baidu-sync-del-${imageId}`;
          notifApi.open({ key, message: '百度后台同步', description: '删除进行中…', duration: 0 });
          let tries = 0;
          const timer = window.setInterval(async () => {
            tries += 1;
            try {
              const res = await getBaiduTaskStatus([imageId]);
              const s = (res.statuses || {})[imageId];
              if (s?.status === 'done') {
                notifApi.success({ key, message: '百度后台同步完成', description: '删除完成', duration: 2 });
                window.clearInterval(timer);
              } else if (s?.status === 'error') {
                notifApi.warning({ key, message: '百度后台同步失败', description: s?.error || '删除失败', duration: 3 });
                window.clearInterval(timer);
              } else if (tries >= 20) {
                notifApi.destroy(key);
                window.clearInterval(timer);
              }
            } catch {}
          }, 1500);
        }
        loadImages(currentPage, searchText);
      } else {
        message.error('删除失败');
      }
    } catch (error) {
      message.error('删除失败，请重试');
    }
  };

  // 批量删除图片
  const batchDeleteImages = async () => {
    if (selectedImages.length === 0) {
      message.warning('请先选择要删除的图片');
      return;
    }

    try {
      // 后端已提供批量删除接口，返回包含 baidu_deleted_count
      const resp = await api.post('/images/batch-delete', selectedImages, {
        headers: { 'Content-Type': 'application/json' },
      });
      const data = resp.data;
      
      if (resp.status >= 200 && resp.status < 300) {
        message.success('已提交删除（百度后台同步）');
        // 仅在开启时显示后台同步详情标签（默认关闭）
        if (SHOW_BAIDU_PROGRESS) {
          const ids = selectedImages.slice();
          const key = `baidu-sync-batch-del`;
          notifApi.open({ key, message: '百度后台同步', description: `删除进行中…`, duration: 0 });
          let tries = 0;
          const timer = window.setInterval(async () => {
            tries += 1;
            try {
              const res = await getBaiduTaskStatus(ids);
              const statuses = res.statuses || {} as Record<number, any>;
              let done = 0, err = 0;
              ids.forEach(id => {
                const s = statuses[id];
                if (s?.status === 'done') done += 1; else if (s?.status === 'error') err += 1;
              });
              const running = Math.max(0, ids.length - done - err);
              notifApi.open({ key, message: '百度后台同步', description: `完成 ${done}/${ids.length}，失败 ${err}，进行中 ${running}`, duration: 0 });
              if (done + err >= ids.length || tries >= 20) {
                window.clearInterval(timer);
                if (err === 0) notifApi.success({ key, message: '百度后台同步完成', description: `全部 ${ids.length} 已完成`, duration: 2 });
                else notifApi.warning({ key, message: '百度后台同步结束', description: `完成 ${done}/${ids.length}，失败 ${err}`, duration: 3 });
              }
            } catch {}
          }, 1500);
        }
        setSelectedImages([]);
        setSelectMode(false);
        loadImages(currentPage, searchText);
        if ((data.error_count || 0) > 0) message.warning(`有 ${data.error_count} 条删除异常，请查看日志`);
      }
    } catch (error) {
      message.error('批量删除失败，请重试');
    }
  };

  // 批量重同步到百度
  const batchResyncBaidu = async () => {
    if (selectedImages.length === 0) {
      message.warning('请先选择要重同步的图片');
      return;
    }
    setResyncing(true);
    try {
      const resp = await api.post('/images/baidu/resync', selectedImages, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (resp.status >= 200 && resp.status < 300) {
        const data = resp.data;
        message.success(`已重同步 ${data.updated} 张，失败 ${data.error_count} 张`);
        loadImages(currentPage, searchText);
        setSelectedImages([]);
        setSelectMode(false);
      } else {
        message.error('重同步失败');
      }
    } catch (e) {
      message.error('重同步失败，请重试');
    } finally {
      setResyncing(false);
    }
  };

  // 查看图片详情
  const viewImage = (image: any) => {
    setPreviewItem(image);
    setPreviewVisible(true);
  };

  // 编辑图片信息
  const editImage = (image: any) => {
    console.log('编辑按钮被点击，图片信息:', image);
    setEditingImage(image);
    
    // 安全地处理标签数据
    const tagsString = Array.isArray(image.tags) ? image.tags.join(',') : '';
    console.log('设置表单值:', {
      description: image.description || '',
      tags: tagsString
    });
    
    form.setFieldsValue({
      description: image.description || '',
      tags: tagsString
    });
    setEditModalVisible(true);
    console.log('编辑模态框应该显示了');
  };

  // 保存编辑的图片信息
  const saveImageInfo = async () => {
    try {
      console.log('开始保存图片信息...');
      console.log('编辑的图片:', editingImage);
      
      const values = await form.validateFields();
      console.log('表单值:', values);
      
      const tags = values.tags ? values.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag) : [];
      console.log('处理后的标签:', tags);
      
      console.log('请求URL:', `/api/images/${editingImage.id}`);

      const response = await api.put(
        `/images/${editingImage.id}`,
        null,
        {
          params: {
            description: values.description || '',
            tags: JSON.stringify(tags)
          }
        }
      );

      console.log('响应状态:', response.status);

      if (response.status >= 200 && response.status < 300) {
        const result = response.data;
        console.log('成功响应:', result);
        message.success('图片信息更新成功');
        setEditModalVisible(false);
        setEditingImage(null);
        form.resetFields();
        loadImages(currentPage, searchText);
      } else {
        console.error('错误响应:', response.status, response.data);
        message.error(`更新失败: ${response.status}`);
      }
    } catch (error) {
      console.error('保存错误:', error);
      message.error('更新失败，请重试');
    }
  };

  // 搜索处理
  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = window.setTimeout(() => {
      loadImages(1, value);
    }, 300);
  };

  // 切换选择模式
  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedImages([]);
  };

  // 全选/取消全选
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const visible = images.filter((img: any) => {
        if (!Array.isArray((img as any).tags) || selectedTags.length === 0) return true;
        const imgTags = (img as any).tags.map((t: any) => String(t)).filter(Boolean);
        return selectedTags.every(t => imgTags.includes(t));
      });
      setSelectedImages(visible.map((img: any) => img.id));
    } else {
      setSelectedImages([]);
    }
  };

  // 单个图片选择
  const handleImageSelect = (imageId: number, checked: boolean) => {
    if (checked) {
      setSelectedImages([...selectedImages, imageId]);
    } else {
      setSelectedImages(selectedImages.filter(id => id !== imageId));
    }
  };


  // 页面加载时获取数据
  useEffect(() => {
    loadImages(1, searchText);
    setCurrentPage(1);
  }, [pageSize]);

  useEffect(() => {
    loadImages();
  }, []);

  // 选择的标签变化时，重置到第1页并服务端筛选
  useEffect(() => {
    setCurrentPage(1);
    loadImages(1, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTags.join(',')]);

  // 根据所选标签进行前端筛选
  const visibleImages = images.filter((img: any) => {
    if (!Array.isArray((img as any).tags) || selectedTags.length === 0) return true;
    const imgTags = (img as any).tags.map((t: any) => String(t)).filter(Boolean);
    return selectedTags.every(t => imgTags.includes(t));
  });

  return (
    <div className="gallery-page">
      {notifCtx}
      <Card title="图片库管理" className="gallery-card">
        {/* 搜索栏 */}
        <div className="search-bar">
          <div className="search-controls" style={{ justifyContent: 'space-between' }}>
            <Space>
              <Select
                mode="tags"
                allowClear
                placeholder="选择或输入标签"
                style={{ minWidth: 220 }}
                value={selectedTags}
                onChange={(vals) => setSelectedTags((vals as string[]).map(v => v.trim()).filter(Boolean))}
                options={availableTags.map(t => ({ value: t, label: t }))}
              />
              <Search
                placeholder="搜索图片名称或描述"
                allowClear
                enterButton={<SearchOutlined />}
                size="large"
                onSearch={handleSearch}
                style={{ maxWidth: 360 }}
              />
            </Space>
            <Space>
              <Button 
                icon={<SelectOutlined />}
                onClick={toggleSelectMode}
                type={selectMode ? 'primary' : 'default'}
              >
                {selectMode ? '取消选择' : '批量选择'}
              </Button>
              <Select
                value={pageSize}
                style={{ width: 120 }}
                onChange={(v) => setPageSize(v)}
                options={[
                  { value: 5, label: '每页 5' },
                  { value: 20, label: '每页 20' },
                  { value: 50, label: '每页 50' },
                  { value: 100, label: '每页 100' },
                ]}
              />
              {selectMode && (
                <>
                  <Checkbox
                    checked={selectedImages.length === visibleImages.length && visibleImages.length > 0}
                    indeterminate={selectedImages.length > 0 && selectedImages.length < visibleImages.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  >
                    全选
                  </Checkbox>
                  <Popconfirm
                    title={`确定删除选中的 ${selectedImages.length} 张图片吗？`}
                    onConfirm={batchDeleteImages}
                    okText="确定"
                    cancelText="取消"
                    disabled={selectedImages.length === 0}
                  >
                    <Button 
                      danger
                      disabled={selectedImages.length === 0}
                    >
                      批量删除 ({selectedImages.length})
                    </Button>
                  </Popconfirm>
                  <Button 
                    icon={<ReloadOutlined />}
                    onClick={batchResyncBaidu}
                    loading={resyncing}
                    disabled={selectedImages.length === 0}
                    type="default"
                  >
                    重同步到百度
                  </Button>
                  <Button
                    type="primary"
                    disabled={selectedImages.length === 0}
                    onClick={() => setBulkModalVisible(true)}
                  >
                    批量修改
                  </Button>
                </>
              )}
            </Space>
          </div>
          <div className="gallery-stats">
            共 {total} 张图片
          </div>
        </div>

        {/* 图片网格（平铺） */}
        <Spin spinning={loading} size="large">
          {visibleImages.length > 0 ? (
            <div className="image-grid-tiles">
              {visibleImages.map(image => (
                <Card
                  key={image.id}
                  hoverable
                  className={`image-card ${selectMode ? 'select-mode' : ''} ${selectedImages.includes(image.id) ? 'selected' : ''}`}
                  cover={
                    <div className="image-cover-wrapper" style={{ position: 'relative' }}>
                      {selectMode && (
                        <Checkbox
                          className="image-checkbox"
                          checked={selectedImages.includes(image.id)}
                          onChange={(e) => handleImageSelect(image.id, e.target.checked)}
                        />
                      )}
                      {/* 百度已入库标识 */}
                      {((image as any).baidu_synced || !!(image as any).baidu_sign) && (
                        <div style={{
                          position: 'absolute',
                          top: 6,
                          left: 6,
                          background: 'rgba(64,158,255,0.9)',
                          color: '#fff',
                          fontSize: 12,
                          padding: '2px 6px',
                          borderRadius: 4,
                          zIndex: 2
                        }}>已入库</div>
                      )}
                      <img
                        src={(image as any).thumb_url || (image as any).file_path}
                        alt={image.filename}
                        className="gallery-image"
                        onClick={() => viewImage(image)}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOTk5IiBmb250LWZhbWlseT0ibW9ub3NwYWNlIiBmb250LXNpemU9IjE0cHgiPuWbvueJh+WKoOi9veWksei0pTwvdGV4dD48L3N2Zz4=';
                        }}
                      />
                    </div>
                  }
                  actions={[
                    <EyeOutlined key="view" onClick={() => viewImage(image)} />,
                    <EditOutlined key="edit" onClick={() => editImage(image)} />,
                    <Popconfirm title="确定删除这张图片吗？" onConfirm={() => deleteImage(image.id)} okText="确定" cancelText="取消">
                      <DeleteOutlined key="delete" className="delete-icon" />
                    </Popconfirm>
                  ]}
                >
                  <div className="image-info">
                    <h4 className="image-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {image.filename}
                      {(((image as any).baidu_synced) || !!(image as any).baidu_sign) && (
                        <Tag color="blue" style={{ marginLeft: 4 }}>已入库</Tag>
                      )}
                    </h4>
                    <p className="image-desc">{(image as any).description || '无描述'}</p>
                    <div className="image-tags">
                      {Array.isArray((image as any).tags) && (image as any).tags.length > 0 ? (
                        (image as any).tags.map((tag: string, index: number) => (
                          <Tag key={index} color="blue" style={{
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                            verticalAlign: 'middle'
                          }}>
                            {tag}
                          </Tag>
                        ))
                      ) : (
                        <span className="no-tags">无标签</span>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
          <div className="empty-gallery">
            <p>暂无图片，请先上传图片</p>
          </div>
        )}

          {/* 分页 */}
          {total > 5 && (
            <div className="pagination-container">
              <Pagination
                current={currentPage}
                total={total}
                pageSize={pageSize}
                onChange={(page) => {
                  setCurrentPage(page);
                  loadImages(page, searchText);
                }}
                showSizeChanger={false}
                showQuickJumper
                showTotal={(total, range) => 
                  `第 ${range[0]}-${range[1]} 张，共 ${total} 张图片`
                }
              />
            </div>
          )}
        </Spin>
      </Card>

      {/* 批量编辑模态框 */}
      <Modal
        title={`批量修改（${selectedImages.length} 张）`}
        open={bulkModalVisible}
        onCancel={() => { if (!bulkLoading) { setBulkModalVisible(false); setBulkUpdateDesc(false); setBulkUpdateTags(false); setBulkDesc(''); setBulkTags(''); setBulkTagOperation('replace'); }}}
        okButtonProps={{ loading: bulkLoading, disabled: selectedImages.length === 0 || (!bulkUpdateDesc && !bulkUpdateTags) }}
        onOk={async () => {
          if (selectedImages.length === 0) { message.warning('请先选择图片'); return; }
          if (!bulkUpdateDesc && !bulkUpdateTags) { message.warning('请选择至少一项修改内容'); return; }
          setBulkLoading(true);
          try {
            // 先处理标签（如选择了）
            if (bulkUpdateTags) {
              const tags = (bulkTags || '').split(',').map(s => s.trim()).filter(Boolean);
              await batchUpdateTags(selectedImages, tags, bulkTagOperation);
            }
            // 再处理描述（如选择了）：逐个调用统一 meta 接口，避免新增后端接口
            if (bulkUpdateDesc) {
              const desc = (bulkDesc || '').trim();
              for (const id of selectedImages) {
                await updateImageMeta(id, { description: desc });
              }
            }
            message.success('批量更改已提交');
            setBulkModalVisible(false);
            setBulkUpdateDesc(false);
            setBulkUpdateTags(false);
            setBulkDesc('');
            setBulkTags('');
            setBulkTagOperation('replace');
            setSelectedImages([]);
            setSelectMode(false);
            loadImages(currentPage, searchText);
          } catch (e: any) {
            const detail = e?.response?.data?.detail;
            message.error(detail || '批量更改失败，请重试');
          } finally {
            setBulkLoading(false);
          }
        }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Checkbox checked={bulkUpdateDesc} onChange={(e) => setBulkUpdateDesc(e.target.checked)}>更改描述</Checkbox>
            <Input.TextArea
              rows={3}
              placeholder="新描述（留空将设为空）"
              value={bulkDesc}
              onChange={(e) => setBulkDesc(e.target.value)}
              disabled={!bulkUpdateDesc}
              maxLength={200}
              showCount
            />
          </div>
          <div>
            <Checkbox checked={bulkUpdateTags} onChange={(e) => setBulkUpdateTags(e.target.checked)}>更改标签</Checkbox>
            <Space wrap>
              <Select
                value={bulkTagOperation}
                onChange={(v) => setBulkTagOperation(v)}
                options={[{ value: 'replace', label: '替换' }, { value: 'add', label: '追加' }, { value: 'remove', label: '移除' }]}
                style={{ width: 120 }}
                disabled={!bulkUpdateTags}
              />
              <Input
                placeholder="多个标签用逗号分隔"
                value={bulkTags}
                onChange={(e) => setBulkTags(e.target.value)}
                disabled={!bulkUpdateTags}
              />
            </Space>
          </div>
        </Space>
      </Modal>

      {/* 预览大图（可放大） */}
      <Modal
        title={previewItem ? previewItem.filename : '图片预览'}
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={900}
        destroyOnClose
      >
        {previewItem && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 560px', minWidth: 320, textAlign: 'center' }}>
              <AntImage
                src={previewItem.file_path}
                alt={previewItem.filename}
                style={{ maxWidth: '100%', maxHeight: 520, objectFit: 'contain' }}
              />
            </div>
            <div style={{ flex: '1 1 240px', minWidth: 220 }}>
              <div style={{ marginBottom: 8 }}>
                <strong>文件名：</strong>{previewItem.filename}
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>分类：</strong>{(previewItem as any).category || '—'}
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>标签：</strong>
                <div style={{ marginTop: 6 }}>
                  {Array.isArray(previewItem.tags) && previewItem.tags.length > 0 ? (
                    previewItem.tags.map((tag: string, idx: number) => (
                      <Tag key={idx} color="blue" style={{ marginBottom: 6 }}>{tag}</Tag>
                    ))
                  ) : (
                    <span className="no-tags">无标签</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 编辑图片信息模态框 */}
      <Modal
        title="编辑图片信息"
        open={editModalVisible}
        onOk={saveImageInfo}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingImage(null);
          form.resetFields();
        }}
        okText="保存"
        cancelText="取消"
        destroyOnHidden
      >
        {editingImage && (
          <div>
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <img 
                src={editingImage.file_path} 
                alt={editingImage.filename}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '300px', 
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
              />
            </div>
            <Form form={form} layout="vertical">
              <Form.Item 
                label="图片描述" 
                name="description"
                rules={[
                  { max: 200, message: '描述不能超过200个字符' }
                ]}
              >
                <Input.TextArea 
                  rows={3} 
                  placeholder="请输入图片描述"
                  showCount
                  maxLength={200}
                  className="edit-modal-textarea"
                />
              </Form.Item>
              <Form.Item 
                label="标签分类" 
                name="tags"
                help="多个标签用逗号分隔"
              >
                <Input 
                  placeholder="例如：风景,自然,山川"
                  className="edit-modal-input"
                />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* 修改密码 */}
      <Modal
        title="修改密码"
        open={pwdModalVisible}
        onOk={async () => {
          try {
            const vals = await pwdForm.validateFields();
            if (vals.new_password !== vals.new_password2) {
              message.error('两次新密码不一致');
              return;
            }
            await changePassword(vals.old_password, vals.new_password);
            message.success('密码修改成功，请重新登录');
            setPwdModalVisible(false);
            document.cookie = 'token=; Max-Age=0; path=/';
            window.location.href = '/login';
          } catch {}
        }}
        onCancel={() => setPwdModalVisible(false)}
        okText="提交"
        cancelText="取消"
        destroyOnHidden
      >
        <Form form={pwdForm} layout="vertical">
          <Form.Item label="当前密码" name="old_password" rules={[{ required: true, message: '请输入当前密码' }]}>
            <Input.Password placeholder="当前密码" />
          </Form.Item>
          <Form.Item label="新密码" name="new_password" rules={[{ required: true, message: '请输入新密码' }]}>
            <Input.Password placeholder="新密码" />
          </Form.Item>
          <Form.Item label="确认新密码" name="new_password2" rules={[{ required: true, message: '请确认新密码' }]}>
            <Input.Password placeholder="再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GalleryPage;
